
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import linkedinLogo from "@/assets/linkedin.svg";
import AuthBrandPanel from "@/components/marketing/AuthBrandPanel";
import Seo from "@/components/marketing/Seo";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import { z } from "zod";

const formSchema = z.object({
  email: z.string().email({ message: "Please enter a valid email address" }),
  password: z
    .string()
    .min(8, { message: "Password must be at least 8 characters long" }),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  agreeV1: z.literal(true, { errorMap: () => ({ message: "You must agree to the Terms and Privacy Policy" }) }),
});

type FormValues = z.infer<typeof formSchema>;

const Register = () => {
  const { register: registerUser } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      password: "",
      firstName: "",
      lastName: "",
      agreeV1: false,
    },
  });

  const onSubmit = async (values: FormValues) => {
    try {
      setIsSubmitting(true);
      await registerUser(
        values.email,
        values.password,
        values.firstName,
        values.lastName
      );
    } catch (error) {
      console.error("Registration error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const startLinkedInSignup = () => {
    const params = new URLSearchParams({
      intent: "signup",
      return_url: "/getting-started",
    });
    window.location.assign(`/api/auth/linkedin/start?${params.toString()}`);
  };

  return (
    <>
      <Seo
        title="Create Your Account | HiredCoach"
        description="Create a HiredCoach account to practice interviews, get feedback, and keep your job search organized."
        canonicalPath="/register"
        robots="noindex,follow"
      />
      <div className="flex min-h-screen bg-interview-light">
      <div className="w-full md:w-1/2 p-8 flex flex-col justify-center">
        <div className="max-w-md mx-auto w-full space-y-8">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-interview-blue">
              Create Your Account
            </h1>
            <p className="mt-2 text-gray-600">
              Join HiredCoach to get your search organized and walk into interviews better prepared
            </p>
          </div>

          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="space-y-6 animate-fade-in"
            >
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="firstName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>First Name</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="John"
                          className="interview-input"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="lastName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Last Name</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Doe"
                          className="interview-input"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="your.email@example.com"
                        type="email"
                        className="interview-input"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="agreeV1"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-start gap-3">
                      <FormControl>
                        <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                      <div className="space-y-1 leading-none text-sm text-gray-700">
                        <FormLabel className="font-normal">
                          I agree to the <Link to="/agreements" className="text-interview-teal underline">User Agreement</Link> and <Link to="/agreements" className="text-interview-teal underline">Privacy Policy</Link>
                        </FormLabel>
                        <FormMessage />
                      </div>
                    </div>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Password</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="********"
                        type="password"
                        className="interview-input"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button
                type="submit"
                className="w-full h-11 btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Creating account..." : "Create account"}
              </Button>

              <Button
                type="button"
                variant="outline"
                className="w-full h-11"
                onClick={startLinkedInSignup}
                disabled={isSubmitting}
              >
                <img src={linkedinLogo} alt="" aria-hidden="true" className="h-4 w-4" />
                Sign up with LinkedIn
              </Button>
            </form>
          </Form>

          <div className="text-center mt-4">
            <p>
              Already have an account?{" "}
              <Link to="/login" className="text-interview-teal hover:underline">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>

      <AuthBrandPanel
        title="Get the whole hunt under control"
        body="Build your story, target the jobs you care about, and practice before the conversation carries real stakes."
      />

      </div>
    </>
  );
};

export default Register;
